/**
 * Buy/Sell signal logic based on technical analysis
 * Uses RSI and price from TAAPI
 */

import { fetchTAData, TAData } from "./taService";
import { sendTelegramSignal } from "./telegramService";

export type SignalType = "BUY" | "SELL";

export interface TASignal {
  symbol: string;
  type: SignalType;
  reason: string;
  source: "rsi";
  rsi?: number;
  price?: number;
  fearGreed?: number;
}

const RSI_OVERSOLD = 30;
const RSI_OVERBOUGHT = 70;

function evaluateSignals(data: TAData): TASignal[] {
  const signals: TASignal[] = [];
  const rsi = data.indicators.rsi as number | undefined;
  const price = data.indicators.price as number | undefined;

  if (typeof rsi === "number") {
    if (rsi <= RSI_OVERSOLD) {
      signals.push({
        symbol: data.symbol,
        type: "BUY",
        reason: `RSI oversold (${rsi.toFixed(1)} ≤ ${RSI_OVERSOLD})`,
        source: "rsi",
        rsi,
        price,
        fearGreed: data.fearGreed,
      });
    } else if (rsi >= RSI_OVERBOUGHT) {
      signals.push({
        symbol: data.symbol,
        type: "SELL",
        reason: `RSI overbought (${rsi.toFixed(1)} ≥ ${RSI_OVERBOUGHT})`,
        source: "rsi",
        rsi,
        price,
        fearGreed: data.fearGreed,
      });
    }
  }

  return signals;
}

function formatSignal(s: TASignal): string {
  const emoji = s.type === "BUY" ? "🟢" : "🔴";
  let msg = `${emoji} ${s.type}: ${s.symbol}\n${s.reason}`;
  if (s.price != null) msg += `\nPrice: ${s.price >= 1000 ? s.price.toLocaleString(undefined, { maximumFractionDigits: 0 }) : s.price.toFixed(2)}`;
  if (s.rsi != null) msg += ` | RSI: ${s.rsi.toFixed(1)}`;
  if (s.fearGreed != null) msg += ` | F&G: ${s.fearGreed}`;
  return msg;
}

export async function checkAndSendSignals(
  symbols: string[],
  interval = "1h"
): Promise<{ sent: number; signals: TASignal[] }> {
  const allSignals: TASignal[] = [];

  for (const symbol of symbols) {
    try {
      const data = await fetchTAData(symbol, interval);
      const signals = evaluateSignals(data);
      allSignals.push(...signals);
    } catch (err) {
      console.warn(`Signal check failed for ${symbol}:`, err);
    }
  }

  let sent = 0;
  for (const s of allSignals) {
    const key = `ta_${s.type}_${s.symbol.replace("/", "_")}_${s.source}`;
    const ok = await sendTelegramSignal(formatSignal(s), key);
    if (ok) sent++;
  }

  return { sent, signals: allSignals };
}
